import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { FxButton } from '../../../../Action/FxButton';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/signup/edit-pencil.svg';
import { AUTH_STRINGS } from '../../../../../constants/strings';
import JointTenancyCoOwnerReviewInfoCardView from './JointTenancyCoOwnerReviewInfoCardView';
import JointTenancyCoOwnerReviewInfoCardEdit from './JointTenancyCoOwnerReviewInfoCardEdit';
import { getKey } from '../../../../../libs/utils/storageManager';
import { getCustomerUrl } from '../../../../../libs/utils/utils';
import { Logger } from '../../../../../libs/utils/logger';
import HttpClient from '../../../../../libs/utils/httpClient';
import { unflattenObject } from '../../../../../libs/utils/autoSaveField';
import { convertToPhoneDisplayFormat, convertToPhoneRequestFormat, updateComponentData, updateComponentParams } from '../../../../Utils/CommonBaseClass';
import { RegisterComponent } from '../../../../../libs/saga/dataSaga';
import { useDispatch } from 'react-redux';
const httpClient = HttpClient.getClient();

interface owner {
  id: string,
  isPrimaryOwner: boolean,
  firstName: string,
  middleName: string,
  lastName: string,
  fullName: string,
  dob: string,
  mobilePhone: string,
  email: string,
  mailingAddress: [
      {
          id: number,
          addressLine1: string,
          addressLine2: string,
          city: string,
          state: string,
          zip: string,
          isPrimary: boolean,
          usage: {
              isPayorAddress: boolean
          }
      }
  ]
}
/**
 * Author: Shivaraj PP
 * Version: 0.0.1
 * Date: 24 Aug 2023
 * Description: Joint Tenancy CoOwner Review Info Card Component
 * This component is used to display the information of the Business Owner and Co-Owners
 * @param props 
 * @returns 
 */
export const JointTenancyCoOwnerReviewInfoCard = (props: any) => {
  let context : any;
  ({ context, props } = RegisterComponent(props));
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [businessOwnerDetails, setBusinessOwnerDetails] = useState<any>();
  const [autoSavedData, setAutoSavedData] = useState<any>();
  const dispatch = useDispatch();

  if (!props?.owners || props?.owners.length === 0) return null;

  const primaryOwner = props?.owners.find((o: { isPrimaryOwner: any }) => o.isPrimaryOwner);
  const coOwners = props?.owners.filter((o: { isPrimaryOwner: any }) => !o.isPrimaryOwner);
  
  /**
   * This method handles the Edit Button Actionc
   * save the owner data to a global state
   * @param owner 
   * @param index 
   */
  const handleEditClick = (owner: owner, index: number) => {
    updateComponentData(dispatch, 'JT-coOwner-edit-info', { ...owner });
    // setBusinessOwnerDetails(owner);
    fetchOnboardingFormData();
    setEditIndex(index);
  };

  /**
   * Method handles the Cancel button action of the form
   */
  const handleCancel = () => {
    setEditIndex(null);
  };

  /**
   * Method handles the Submit button action of the form
   * @param data 
   * @param req 
   * @param index 
   */
  const onSubmit = (data: any,req:any,index:number) => {
    props.onSubmit(data,req,index)
    setEditIndex(null);
  };

  /**
   * This method fetches the previously saved data from the GET autoSaved API
   * and sets the values in the form fields
   */
  const fetchOnboardingFormData = async () => {
      const previouslySavedData = await getPreviouslySavedDataFromOnboardingApi();
      if (previouslySavedData && previouslySavedData?.data && previouslySavedData?.data?.flatFields) {
          const objectFormatData: any = unflattenObject(previouslySavedData.data.flatFields);
          const formattedData = getBODetailsDataFromApi(objectFormatData, 'autoSavedData');

          const mergedData = mergeBeneficialOwners(
              formattedData.slice(1) ?? [],
              businessOwnerDetails
            );

          setAutoSavedData(mergedData);
      }
  }

  /**
   * This method compares the GET API BO Data and the AutoSave API BO data 
   * to have a more secure version of the BO list
   * @param autoSavedData 
   * @param beneficialOwnerData 
   * @returns 
   */
  const mergeBeneficialOwners = (
      autoSavedData: any[] = [],
      beneficialOwnerData: any[] = []
  ) => {
      const baseData = autoSavedData.length > 0 ? autoSavedData : beneficialOwnerData;

      return baseData.map((base, idx) => {
          const overlay = beneficialOwnerData[idx];
          const merged = { ...base };

          if (overlay) {
          // Always replace ID
          merged.id = overlay.id;

          // Merge other non-empty fields from overlay
          Object.keys(overlay).forEach((key) => {
              if (key === 'id') return; // already handled
                  const val = overlay[key];
              if (val !== undefined && val !== '') {
                  merged[key] = val;
              }
              });
          }
          return merged;
      });
  };

  /**
   * Calling the GET autosave api to get the previously saved data
   * @returns 
   */
  const getPreviouslySavedDataFromOnboardingApi = async () => {
      try {
          let apiName: any = '/onboardingFormData'
          return await httpClient.get(getCustomerUrl(apiName,false));
      } catch (err) {
          Logger.error("JointTenancyCoOwnerReviewInfoCard.tsx", "getPreviouslySavedDataFromOnboardingApi", "error", err);
          return err;
      }
  }

  /**
   * method used to convert customer get  api response in form data format
   * @param data :data from api response
   * @returns
   */
  const getBODetailsDataFromApi = (data: any, origin?: any) => {   
      const coOwner:any = data?.owners?.filter((x:any)=> (!x.isPrimaryOwner || x.isPrimaryOwner === 'false') );         

      return coOwner && coOwner?.length ? coOwner.map((data: any) => {
            let _mailingAddress: any = data.mailingAddress;
            _mailingAddress = _mailingAddress ? _mailingAddress[0] : {};
              return {
                    "id":data.id?data.id:data.id,
                    "firstName": data.firstName ? data.firstName : '',
                    "lastName": data.lastName ? data.lastName : '',
                    "middleName": data['middleName'] ? data['middleName'] : '',
                    "email": data.email ? data.email : '',                         
                    "dob": data.dob ? data.dob : '',
                    "ssn": data?.ssn || data?.last4ssn || '',
                    "itin": data?.taxIdentification?.id || data?.taxIdentification?.idLast4 || '', 
                    "address_id": _mailingAddress.id ? _mailingAddress.id : '',
                    "addressLine1": _mailingAddress.addressLine1 ? _mailingAddress.addressLine1 : '',
                    "addressLine2": _mailingAddress.addressLine2 ? _mailingAddress.addressLine2 : '',
                    "city": _mailingAddress.city ? _mailingAddress.city : '',
                    "state": _mailingAddress.state ? _mailingAddress.state : '',
                    "zip": _mailingAddress.zip ? _mailingAddress.zip : '',
                    "mobilePhone": data.mobilePhone ? origin === 'autoSavedData' ? convertToPhoneRequestFormat(data.mobilePhone) : convertToPhoneDisplayFormat(data.mobilePhone) : '',
                  }
        }) : []
    }

  /**
   * Handling the onChange events on the DOB field
   * @param value 
   */
  
  const renderEditForm = () => (
    <JointTenancyCoOwnerReviewInfoCardEdit id={"joint_tendancy_owner_review_card"} autoSavedData={autoSavedData} index={editIndex} onSubmit={onSubmit} onCancel={handleCancel}/>
  );

/**
 * Author: SHivaraj PP
 * Description: Rendering the CoOwner Card
 * Component: JointTenancyCoOwnerReviewInfoCard
 * 
 * This method creates the basic structure of the CoOwner Card that needs to be shown
 * and then renders the appropriate HTML content
 * @param title 
 * @param owner 
 * @param index 
 * @returns 
 */
  const renderCard = (title: string, owner: any, index = 0) => {
    const address = owner.mailingAddress?.[0] || {};
    const authUserId = getKey("authUserId");
    const showEdit = owner?.id===authUserId;

    if (editIndex === index) {
      return renderEditForm();
    }

    const cardData = {
      items: [
        {
          index: coOwners.length > 1 && !owner.isPrimaryOwner ? index + 1 : undefined,
          name: owner.fullName,
          data: {
            // ssn: { title: 'SSN ', value: `${owner.last4ssn ? '*' + owner.last4ssn : ''}` },
            ...(owner.taxIdentification ? { itin: { title: 'ITIN', value: `${owner.taxIdentification.idLast4 ? '*' + owner.taxIdentification.idLast4 : ''}` } } :
            { ssn: { title: 'SSN ', value: `${owner.last4ssn ? '*' + owner.last4ssn : ''}` } }),
            address: {
              title: '',
              value: `${address.addressLine1}, ${address.city}, ${address.state}, Zip - ${address.zip}`
            },
            email: { title: '', value: owner.email }
          }
        }
      ]
    };
    
    return (
      <Grid container key={index} xs={12} className={'fx-review-info-card fx-margin-bottom-2'}>
        <Grid item xs={12} className={'fx-review-info-card-header'} container justifyContent={'space-between'}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item><Typography variant="h3">{title}</Typography></Grid>
            {showEdit && (
              <Grid item display={'flex'}>
                <FxButton
                  startIcon={<EditIcon />}
                  id="login-signin"
                  type="button"
                  className="fx-review-info-card-header-title"
                  onClick={() => handleEditClick(owner, index)}
                >
                  Edit
                </FxButton>
              </Grid>
            )}
          </Grid>
        </Grid>
        {cardData.items.map((item: any, idx: number) => (
          <JointTenancyCoOwnerReviewInfoCardView item={item} index={idx}/>
        ))}
      </Grid>
    );
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={12}>
        {primaryOwner && renderCard('Primary Owner Information', primaryOwner, -1)}
        {coOwners.map((coOwner: any, idx: number) => renderCard('Co-Owner Information', coOwner, idx))}
      </Grid>
    </Grid>
  );
};
